import React, { useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "../lib/api";
import { ensureConnected, findDefaultPrinter, printRaw, isQzActive } from "../lib/qz";
import { toast } from "sonner";
import { X, Printer, AlertTriangle, CheckCircle2, Wifi, Usb } from "lucide-react";

/**
 * Single-sticker print dialog with HTML preview + USB (QZ Tray) / Network routing.
 * Props:
 *   inventory: row from /api/inventory  (must have id, grn_number, etc.)
 *   onClose: ()=>void
 *   defaultLabelSizeId?: string
 */
export default function StickerPrintDialog({ inventory, onClose, defaultLabelSizeId }) {
  const [sizes, setSizes] = useState([]);
  const [sizeId, setSizeId] = useState(defaultLabelSizeId || "");
  const [printerCfg, setPrinterCfg] = useState(null);
  const [qzReady, setQzReady] = useState(false);
  const [qzPrinter, setQzPrinter] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    Promise.all([
      api.get("/sticker-sizes"),
      api.get("/settings/sticker-printer"),
    ]).then(([sz, pc]) => {
      setSizes(sz.data);
      setPrinterCfg(pc.data);
      if (!sizeId && sz.data[0]) setSizeId(sz.data[0].id);
    });
  }, []);  // eslint-disable-line

  // Probe QZ Tray on mount when in USB mode.
  useEffect(() => {
    if (!printerCfg || printerCfg.connection !== "usb") return;
    let alive = true;
    (async () => {
      const ok = await ensureConnected();
      if (!alive) return;
      setQzReady(ok);
      if (ok) {
        const p = await findDefaultPrinter();
        if (alive) setQzPrinter(p);
      }
    })();
    const t = setInterval(() => { if (alive) setQzReady(isQzActive()); }, 4000);
    return () => { alive = false; clearInterval(t); };
  }, [printerCfg]);

  const size = sizes.find((s) => s.id === sizeId);
  const mode = printerCfg?.connection || "usb";
  const canPrint = !!size && (mode === "network" ? !!printerCfg?.ip : (qzReady && !!qzPrinter));

  const handlePrint = async () => {
    if (!size || !inventory) return;
    setBusy(true);
    try {
      if (mode === "network") {
        await api.post("/print/sticker/network", {
          inventory_id: inventory.id, label_size_id: sizeId,
        });
        toast.success(`Sent to network printer ${printerCfg.ip}:${printerCfg.port}`);
      } else {
        const r = await api.post("/print/sticker/tspl", {
          inventory_id: inventory.id, label_size_id: sizeId,
        });
        await printRaw(qzPrinter, r.data.tspl);
        toast.success(`Sent to ${qzPrinter} via QZ Tray`);
      }
      onClose?.();
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || e.message || "Print failed");
    }
    setBusy(false);
  };

  return (
    <div className="fixed inset-0 z-50 bg-[#0D2B5E]/40 backdrop-blur-sm flex items-center justify-center p-8" data-testid="sticker-print-dialog">
      <div className="bg-white border border-[#EEEEEE] rounded-sm shadow-2xl max-w-3xl w-full p-6">
        <div className="flex items-start justify-between border-b border-[#EEEEEE] pb-3 mb-4">
          <div>
            <div className="text-[10px] tracking-[0.3em] uppercase font-bold text-[#0D2B5E]/60">Print sticker · TSC TTP-244 Pro</div>
            <div className="text-base font-bold text-[#0D2B5E] font-mono">{inventory?.grn_number} · Box {inventory?.box_no}/{inventory?.total_boxes}</div>
          </div>
          <button onClick={onClose} className="text-[#0D2B5E]/60 hover:text-[#C62828] p-1.5 rounded-sm" data-testid="sticker-print-close"><X size={18}/></button>
        </div>

        {/* Status banner */}
        <StatusBanner mode={mode} qzReady={qzReady} qzPrinter={qzPrinter} ip={printerCfg?.ip} port={printerCfg?.port}/>

        <div className="grid grid-cols-2 gap-6 mt-4">
          <div>
            <label className="block text-[10px] tracking-widest uppercase font-bold text-[#0D2B5E]/60 mb-1">Label size</label>
            <select value={sizeId} onChange={(e) => setSizeId(e.target.value)} className="w-full border border-[#EEEEEE] px-3 py-2 text-sm rounded-sm" data-testid="sticker-size-select">
              {sizes.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
            </select>
            <div className="mt-4">
              <div className="text-[10px] tracking-widest uppercase font-bold text-[#0D2B5E]/60 mb-2">QR encodes (pipe format — Outward Scan compatible)</div>
              <div className="bg-[#0D2B5E] text-white text-[11px] font-mono p-2 rounded-sm break-all">
                {`${inventory?.grn_number}|${inventory?.lot_no}|${inventory?.material_code}|BOX-${inventory?.box_no}`}
              </div>
            </div>
          </div>
          <StickerPreview inventory={inventory} size={size}/>
        </div>

        <div className="flex justify-end gap-2 mt-6 pt-4 border-t border-[#EEEEEE]">
          <button onClick={onClose} className="px-4 py-2 border border-[#EEEEEE] text-sm rounded-sm" data-testid="sticker-print-cancel">Cancel</button>
          <button onClick={handlePrint} disabled={!canPrint || busy} className="px-5 py-2 bg-[#1565C0] hover:bg-[#0D2B5E] disabled:bg-[#BBB] text-white text-sm rounded-sm font-bold uppercase tracking-wider flex items-center gap-2" data-testid="sticker-print-confirm">
            <Printer size={14}/>{busy ? "Sending…" : "Confirm and Print"}
          </button>
        </div>
      </div>
    </div>
  );
}

function StatusBanner({ mode, qzReady, qzPrinter, ip, port }) {
  if (mode === "network") {
    return ip ? (
      <div className="bg-[#2E7D32]/10 border border-[#2E7D32]/30 px-4 py-2 rounded-sm flex items-center gap-2 text-sm text-[#2E7D32]" data-testid="sticker-printer-status-network">
        <Wifi size={14}/> Network mode · {ip}:{port || 9100}
      </div>
    ) : (
      <div className="bg-[#FFB74D]/10 border border-[#FFB74D]/40 px-4 py-2 rounded-sm flex items-center gap-2 text-sm text-[#E65100]" data-testid="sticker-printer-status-no-ip">
        <AlertTriangle size={14}/> Network mode selected but printer IP not configured. Ask Super Admin to set it under Settings → Printer.
      </div>
    );
  }
  if (qzReady && qzPrinter) {
    return (
      <div className="bg-[#2E7D32]/10 border border-[#2E7D32]/30 px-4 py-2 rounded-sm flex items-center gap-2 text-sm text-[#2E7D32]" data-testid="sticker-printer-status-ready">
        <CheckCircle2 size={14}/> Printer Ready · QZ Tray connected · {qzPrinter}
      </div>
    );
  }
  return (
    <div className="bg-[#C62828]/10 border border-[#C62828]/30 px-4 py-2 rounded-sm flex items-start gap-2 text-sm text-[#C62828]" data-testid="sticker-printer-status-not-ready">
      <Usb size={14} className="mt-0.5"/>
      <div className="flex-1">
        <div className="font-bold">Label printer service not running.</div>
        <div className="text-xs text-[#C62828]/80 mt-0.5">Please start <strong>QZ Tray</strong> on this workstation. Free download at <a className="underline" href="https://qz.io/download/" target="_blank" rel="noreferrer">qz.io/download</a>.</div>
      </div>
    </div>
  );
}

function StickerPreview({ inventory, size }) {
  // Mirror of backend printing.py — locked grid (V3):
  //   Top-left:  BOX, MAT, QTY (3 rows beside the QR)
  //   Top-right: QR — fixed pixel size (cell=4 → 116 dots ≈ 14.5 mm)
  //   Bottom:    GRN, LOT (full label width)
  const PX_PER_MM = 5;
  const w = (size?.width_mm || 38) * PX_PER_MM;
  const h = (size?.height_mm || 38) * PX_PER_MM;
  const dpi = 203;
  const dotsPerMM = dpi / 25.4;
  const Wd = (size?.width_mm || 38) * dotsPerMM;
  const Hd = (size?.height_mm || 38) * dotsPerMM;
  const MARGIN = 8;
  const ROW_GAP = 4;
  const QR_CELL = 3;              // matches backend QR_CELL
  const QR_SIDE = QR_CELL * 29;   // dots
  const QR_LEFT_OFFSET = 20;      // Wave-21 Ch-1: shift QR inward by ~2.5 mm

  // dots → px scale
  const k = PX_PER_MM / dotsPerMM;

  // QR (top-right, shifted left by QR_LEFT_OFFSET)
  const qrXd = Wd - QR_SIDE - MARGIN - QR_LEFT_OFFSET;
  const qrYd = MARGIN;

  // Top-left zone — Wave-21: MAT moved to the bottom, so only BOX + QTY here.
  const topZoneX = MARGIN;
  const topZoneW = qrXd - MARGIN - topZoneX;

  const boxVal = `${inventory?.box_no || 1}/${inventory?.total_boxes || 1}`;
  const matVal = String(inventory?.material_code || "");
  const qtyVal = String(inventory?.qty || "0");
  const grnVal = String(inventory?.grn_number || "");
  const lotVal = String(inventory?.lot_no || "");

  // Top-zone font (stays at "2" per Wave-20 Ch-2).
  const FONTS = { 1: { w: 8, h: 12 }, 2: { w: 10, h: 16 }, 3: { w: 12, h: 20 } };
  const longestTop = Math.max(boxVal.length, qtyVal.length);
  const fitTop = (code) => (4 + Math.max(1, longestTop)) * FONTS[code].w <= topZoneW;
  const topFontCode = fitTop(2) ? 2 : 1;
  const topFont = FONTS[topFontCode];
  const topRowH = topFont.h + ROW_GAP;

  // Vertically center the 2 top rows inside the QR's height.
  const twoRowsH = topRowH * 2 - ROW_GAP;
  const topFirstY = qrYd + Math.max(0, (QR_SIDE - twoRowsH) / 2);

  // Bottom zone — Wave-21: MAT / GRN / LOT stacked, font ladder starts at "2".
  const longestBot = Math.max(matVal.length, grnVal.length, lotVal.length);
  const bottomZoneW = Wd - 2 * MARGIN;
  const fitBot = (code) => Math.max(1, longestBot) * FONTS[code].w <= bottomZoneW;
  const botFontCode = fitBot(2) ? 2 : 1;
  const botFont = FONTS[botFontCode];
  const botRowH = botFont.h + ROW_GAP;
  const bottomTop = qrYd + QR_SIDE + ROW_GAP * 2;
  const matYd = bottomTop + botRowH;      // MAT at GRN's old slot
  const grnYd = matYd + botRowH;          // GRN shifted down one row
  const lotYd = grnYd + botRowH;          // LOT one row below GRN

  const renderRow = (yDots, fontDef, text, key) => (
    <div key={key} style={{
      position: "absolute",
      left: MARGIN * k,
      top: yDots * k,
      fontSize: fontDef.h * k,
      lineHeight: 1,
      color: "#0D2B5E",
      fontWeight: 700,
      letterSpacing: 0.3,
      whiteSpace: "nowrap",
    }}>{text}</div>
  );

  return (
    <div data-testid="sticker-preview-area">
      <div className="text-[10px] tracking-widest uppercase font-bold text-[#0D2B5E]/60 mb-2">Preview · {size?.name || "—"}</div>
      <div className="bg-[#F5F5F5] p-3 rounded-sm flex items-start justify-center">
        <div className="bg-white border-2 border-[#0D2B5E] relative"
             style={{
               width: w, height: h,
               fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace",
               overflow: "hidden",
               boxSizing: "border-box",
             }}>
          {/* QR (top-right, fixed size) */}
          <div style={{
            position: "absolute",
            left: qrXd * k, top: qrYd * k,
            width: QR_SIDE * k, height: QR_SIDE * k,
            background: "repeating-conic-gradient(#0D2B5E 0% 25%, #fff 0% 50%) 50% / 5px 5px",
            border: "1px solid #0D2B5E",
          }} title="QR placeholder" data-testid="sticker-preview-qr"/>

          {/* Top-left rows (Wave-21: MAT moved to bottom; only BOX + QTY up top) */}
          {renderRow(topFirstY,            topFont, `BOX ${boxVal}`, "box")}
          {renderRow(topFirstY + topRowH,  topFont, `QTY ${qtyVal}`, "qty")}

          {/* Bottom rows (full width, raw values — MAT first, then GRN, then LOT). */}
          {/* Wave-22: LOT renders ~2 pt smaller — next notch down from botFont. */}
          {renderRow(matYd, botFont, matVal, "mat")}
          {renderRow(grnYd, botFont, grnVal, "grn")}
          {renderRow(lotYd, botFont, lotVal, "lot")}
        </div>
      </div>
      <div className="text-[10px] text-[#0D2B5E]/50 mt-2">
        Locked grid · Top-Left: BOX/QTY · Top-Right: fixed QR · Bottom: MAT/GRN/LOT
      </div>
    </div>
  );
}
